import React, { useState } from 'react';
import styles from './FileExtraction.module.css';
import BackgroundContainer from '../ui-components/BackgroundContainer';
import Alert from '../ui-components/Alert';
import { IAITask } from '../../models/aiTaskModel';
import FileSelector from '../files/FileSelector';
import AITaskConfiguration from './AITaskConfiguration';
import { extractFileWithTaskStreaming, extractFileWithTask } from '../../services/api/fileExtractionService';
import { executeTaskStreaming } from '../../services/api/aiTaskService';

interface FileExtractionProps {
    onExtractionComplete: (data: any) => void;
    onExtractionStart?: () => void;
    ownerType?: 'department' | 'manufacturer' | 'user' | 'nordinsight';
    allowedTypes?: {
        document?: boolean;
        image?: boolean;
        video?: boolean;
    };
    label?: string;
    manufacturerName?: string;
    disabled?: boolean;
    disabledMessage?: string;
    aiTasks: IAITask[];
}

const FileExtraction: React.FC<FileExtractionProps> = ({
    onExtractionComplete,
    onExtractionStart,
    ownerType = 'user',
    allowedTypes = { document: true, image: false, video: false },
    label = 'Select File for Extraction',
    manufacturerName,
    disabled = false,
    disabledMessage = 'Extraction is currently disabled',
    aiTasks,
}) => {
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);

    // AI Extraction State
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionError, setExtractionError] = useState<string | null>(null);
    const [isStreaming, setIsStreaming] = useState(false)
    const [output, setOutput] = useState('');

    // Handle selection of files from the dropdown
    const handleFilesSelected = (fileIds: string[]) => {
        setSelectedFiles(fileIds);
        setExtractionError(null);
    };

    const handleExtraction = async (task: IAITask, formValues: Record<string, any>) => {
        setExtractionError(null);
        if (selectedFiles.length === 0) {
            setExtractionError('Please select a file first.');
            return;
        }

        const fileId = selectedFiles[0];

        setIsExtracting(true);
        if (onExtractionStart) onExtractionStart();

        try {
            // const result = await extractFileWithTask(task.name, fileId, formValues);
            // console.log('Extraction result:', result);
            // onExtractionComplete(JSON.parse(result.output));


            const stream = await extractFileWithTaskStreaming(task.name, fileId, formValues);

            for await (const chunk of stream) {

                // Assuming your server sends { content: "text chunk" }
                if (chunk.content) {
                    setOutput((prev) => prev + chunk.content);
                }

                // If the server sends the final execution payload at the end
                if (chunk.status === 'success') {
                    console.log('Extraction result:', chunk);
                    onExtractionComplete(JSON.parse(chunk.output));
                }
            }
        } catch (error: any) {
            setExtractionError('Timeout: File too large');
            console.log(error)
        } finally {
            setIsExtracting(false);
        }
    };

    return (

        <BackgroundContainer>
            <div className={styles.header}>
                <h3>{label}</h3>
            </div>
            <FileSelector
                onFilesSelected={handleFilesSelected}
                ownerType={ownerType}
                manufacturerName={manufacturerName}
                allowedTypes={allowedTypes}
                label={`Current Manufacturer: ${manufacturerName || 'None'}`}
                disabled={disabled}
                disabledMessage={disabledMessage}
                allowMultiple={false}
            />

            {selectedFiles.length > 0 && (
                <div className={styles.separator}>
                    {extractionError && (
                        <Alert
                            title="Extraction Error"
                            variant="destructive"
                            onClose={() => setExtractionError(null)}
                        >
                            <p>{extractionError}</p>
                        </Alert>
                    )}

                    <AITaskConfiguration
                        aiTasks={aiTasks}
                        isExtracting={isExtracting}
                        onExtract={handleExtraction}
                    />
                </div>
            )}
        </BackgroundContainer>
    );
};

export default FileExtraction;
